import React, { useMemo } from "react";

import { TimelineChart as TimelineChartComponent } from "heat-next";

import { useFeatureToggles } from "@/context/feature-toggles";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

import { Features } from "@/enums/features";

import { sanitizeAndAlignCharts } from "@/utils/sanitizeTimeline";

function findLayoutColConfiguration(
    gridLayoutData: unknown,
    componentId: string,
): Record<string, unknown> | undefined {
    if (!Array.isArray(gridLayoutData)) return undefined;

    for (const row of gridLayoutData) {
        const cols = (row as { cols?: unknown[] })?.cols;
        if (!Array.isArray(cols)) continue;

        for (const col of cols) {
            const c = col as { component?: string; configuration?: Record<string, unknown> };
            if (c.component === componentId) {
                return c.configuration;
            }
        }
    }

    return undefined;
}

const timelineMockDateRange = {
    from: "2025-02-17T15:36:37",
    to: "2025-02-17T15:43:52",
};

interface TimelineChartProps {
    subDimensionsData?: any;
    isInsideModal?: boolean;
}

const TimelineChart: React.FC<TimelineChartProps> = ({ subDimensionsData, isInsideModal }) => {
    const { isFeatureActive } = useFeatureToggles();
    const isSymbolToggleActive = isFeatureActive(Features.SymbolToggle);

    const {
        combinedCustomChartsData,
        isFetchingCombinedCustomCharts,
        isErrorCombinedCustomCharts,
        gridLayoutData,
    } = useSessionDetailsContext();

    const graphOnTop = useMemo(() => {
        const timelineChart = combinedCustomChartsData?.find((c: any) => c.graphOnTop !== undefined);
        return Boolean(timelineChart?.graphOnTop);
    }, [combinedCustomChartsData]);

    const hasCombinedCustomChartsData = () => {
        return combinedCustomChartsData?.some((entry: any) => {
            return entry?.datasets?.some((dataset: any) => {
                return (Array.isArray(dataset?.data) && dataset?.data?.length > 0) ?? false;
            });
        });
    };

    const isEmptyState = () => {
        if (subDimensionsData) {
            return !subDimensionsData;
        } else if (
            !isErrorCombinedCustomCharts &&
            (!combinedCustomChartsData ||
                combinedCustomChartsData === null ||
                combinedCustomChartsData?.length === 0 ||
                !hasCombinedCustomChartsData)
        ) {
            return true;
        }
    };

    if (isFetchingCombinedCustomCharts) {
        return <ContentLoader height="22.6875rem" />;
    }

    if (isErrorCombinedCustomCharts && !isFetchingCombinedCustomCharts) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="19.6875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:timeline-chart:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="19.6875rem"
                />
            </ComponentWrapper>
        );
    }

    const sanitizedData = sanitizeAndAlignCharts(
        isInsideModal ? subDimensionsData : combinedCustomChartsData,
    );

    return (
        <TimelineChartComponent
            data={{
                timelineData: sanitizedData as never,
                from: timelineMockDateRange.from,
                to: timelineMockDateRange.to,
            }}
            isSymbolToggleActive={isSymbolToggleActive}
            graphOnTop={graphOnTop}
        />
    );
};

export default TimelineChart;
